// =============================================================================
// charSheets.ts (src/client/presentation) — Sprite sheets desenhados para os
// personagens (todo o elenco jogável tem atlas; o pixel art procedural de
// sprites.ts segue como fallback enquanto o PNG carrega).
//
// Cada atlas é gerado por scripts/build_char_sheets.py a partir de
// references/<nome>_sprites.png: grade 6x4 de células 64x64 em
// /assets/<nome>_sheet.png. Layout padrão (prompt em docs da PR #35):
//   linha 0 = frente (cols 0-2: passo, parado, passo) e costas (cols 3-5 idem)
//   linha 1 = perfil OLHANDO PRA ESQUERDA, ciclo de 6 frames (direita = espelho)
//   linhas 2-3 = vistas 3/4 — ficam de reserva (o jogo usa 4 direções)
// Sheets gerados por IA variam: confira os lados frame a frame antes de
// registrar um personagem novo e, se preciso, dê um mapa próprio a ele.
//
// SKINS (issue #24): as variantes visuais são geradas AQUI, proceduralmente —
// o atlas base passa por um filtro de pixels em canvas (recolorir terno pra
// macacão de presidiário, empalidecer o vampiro...) preservando todos os
// frames e animações. O resultado vira um segundo atlas em memória.
//
// O PNG carrega assíncrono: charSheet() devolve null até a textura estar
// pronta, e o Renderer usa o sprite procedural como fallback nesse meio tempo.
//
// PIXI é um global (carregado via <script> em /vendor); os tipos vêm de
// globals.d.ts — o import abaixo é type-only e some no bundle.
// =============================================================================

import type { BaseTexture, Texture } from 'pixi.js';
import { DEFAULT_SKIN } from '../../shared/skins';

const CELL = 64;

// 'side' é o perfil OLHANDO PRA ESQUERDA; a direita é o mesmo frame espelhado
export type SheetDir = 'down' | 'up' | 'side';

export interface CharSheet {
  walk: Record<SheetDir, Texture[]>;
  idle: Record<SheetDir, Texture>;
}

// Frames [col, row] na grade do atlas, na ordem do ciclo de caminhada
const DEFAULT_WALK: Record<SheetDir, [number, number][]> = {
  down: [[0, 0], [1, 0], [2, 0], [1, 0]],
  up: [[3, 0], [4, 0], [5, 0], [4, 0]],
  side: [[0, 1], [1, 1], [2, 1], [3, 1], [4, 1], [5, 1]],
};
const DEFAULT_IDLE: Record<SheetDir, [number, number]> = {
  down: [1, 0], up: [4, 0], side: [1, 1],
};

// Personagens com atlas no layout padrão
const SHEET_CHARS = [
  'bolsonaro', 'lula', 'dilma',
  'datena', 'marinasilva', 'pablomarcal', 'padrekelmon', 'temer',
] as const;

const sheets = new Map<string, CharSheet>();

// Chave interna: personagem puro ou 'char:skin' pras variantes
const sheetKey = (charId: string, skinId: string): string =>
  skinId === DEFAULT_SKIN ? charId : `${charId}:${skinId}`;

// Fatia um canvas/imagem 6x4 em texturas de célula no layout padrão
function registerSheet(key: string, base: BaseTexture,
  walk: Record<SheetDir, [number, number][]>, idle: Record<SheetDir, [number, number]>): void {
  const tex = ([col, row]: [number, number]): Texture =>
    new PIXI.Texture(base, new PIXI.Rectangle(col * CELL, row * CELL, CELL, CELL));
  sheets.set(key, {
    walk: { down: walk.down.map(tex), up: walk.up.map(tex), side: walk.side.map(tex) },
    idle: { down: tex(idle.down), up: tex(idle.up), side: tex(idle.side) },
  });
}

function loadSheet(charId: string, url: string, walk: Record<SheetDir, [number, number][]>,
  idle: Record<SheetDir, [number, number]>): void {
  const base = PIXI.BaseTexture.from(url, { scaleMode: PIXI.SCALE_MODES.NEAREST });
  const build = (): void => registerSheet(charId, base, walk, idle);
  if (base.valid) build();
  else base.once('loaded', build);
}

// -----------------------------------------------------------------------------
// Filtros de skin: transformações de pixel aplicadas ao atlas inteiro.
// Classificação por cor (tom de pele, tecido escuro "frio", vermelho...) —
// robusta o suficiente pros sheets desenhados sem mapear pixel a pixel.
// -----------------------------------------------------------------------------
type SkinFilter = (img: ImageData) => void;

const lum = (r: number, g: number, b: number): number => 0.299 * r + 0.587 * g + 0.114 * b;
const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

// Tecido escuro e "frio" (terno/calça azulada): nem contorno, nem sapato preto
const isCoolDark = (r: number, g: number, b: number): boolean => {
  const l = lum(r, g, b);
  return b >= r + 6 && l >= 36 && l <= 112;
};
// Vermelho forte (camisa do Lula, gravata do Temer)
const isRed = (r: number, g: number, b: number): boolean =>
  r > 110 && r > g * 2 && r > b * 2;
// Verde de gravata (Bolsonaro)
const isGreen = (r: number, g: number, b: number): boolean =>
  g > r + 20 && g > b + 20;
// Tom de pele quente (todos os sheets usam a mesma família de tons)
const isSkinTone = (r: number, g: number, b: number): boolean =>
  r > 140 && g > 80 && r > g + 25 && g > b + 25;

// Uniforme da Papuda: toda a roupa (terno, calça, camisa vermelha, gravata)
// vira macacão laranja com listras horizontais mais escuras
const presidiario: SkinFilter = (img) => {
  const d = img.data;
  const w = img.width;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] < 40) continue;
    const r = d[i], g = d[i + 1], b = d[i + 2];
    if (!(isCoolDark(r, g, b) || isRed(r, g, b) || isGreen(r, g, b))) continue;
    const l = lum(r, g, b);
    // Sombra do tecido original vira sombra da laranja (shading preservado)
    let t = clamp01((l - 30) / 90);
    const y = Math.floor(i / 4 / w);
    if (y % 6 < 3) t *= 0.62;  // listras horizontais do uniforme
    d[i] = Math.round(lerp(154, 245, t));
    d[i + 1] = Math.round(lerp(76, 134, t));
    d[i + 2] = Math.round(lerp(10, 26, t));
  }
};

// Conde da Madrugada: palidez de cripta, terno tingido de púrpura e gravata
// escurecida pra cor de sangue coagulado
const vampiro: SkinFilter = (img) => {
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] < 40) continue;
    const r = d[i], g = d[i + 1], b = d[i + 2];
    if (isSkinTone(r, g, b)) {
      const t = clamp01(lum(r, g, b) / 210);
      d[i] = Math.round(lerp(140, 236, t));
      d[i + 1] = Math.round(lerp(146, 238, t));
      d[i + 2] = Math.round(lerp(160, 246, t));
    } else if (isRed(r, g, b)) {
      d[i] = Math.round(r * 0.72);
      d[i + 1] = Math.round(g * 0.5);
      d[i + 2] = Math.round(b * 0.5);
    } else if (isCoolDark(r, g, b)) {
      d[i] = Math.round(r * 0.85);
      d[i + 1] = Math.round(g * 0.55);
      d[i + 2] = Math.min(255, Math.round(b * 1.1 + 8));
    }
  }
};

// Skin -> filtro, por personagem (ids de shared/skins.ts)
const SHEET_SKIN_FILTERS: Record<string, Record<string, SkinFilter>> = {
  lula: { papuda: presidiario },
  bolsonaro: { papuda: presidiario },
  temer: { conde: vampiro },
};

// Canvas do atlas de cada skin (pro DOM e pros previews) + dataURL memoizado
const skinCanvases = new Map<string, HTMLCanvasElement>();
const skinURLs = new Map<string, string>();

function loadSkinSheets(charId: string, url: string, walk: Record<SheetDir, [number, number][]>,
  idle: Record<SheetDir, [number, number]>): void {
  const filters = SHEET_SKIN_FILTERS[charId];
  if (!filters) return;
  const img = new Image();
  img.src = url;
  img.onload = () => {
    for (const [skinId, filter] of Object.entries(filters)) {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const g = canvas.getContext('2d')!;
      g.drawImage(img, 0, 0);
      const data = g.getImageData(0, 0, canvas.width, canvas.height);
      filter(data);
      g.putImageData(data, 0, 0);
      const key = sheetKey(charId, skinId);
      skinCanvases.set(key, canvas);
      registerSheet(key, new PIXI.BaseTexture(canvas, { scaleMode: PIXI.SCALE_MODES.NEAREST }), walk, idle);
    }
  };
}

for (const id of SHEET_CHARS) {
  loadSheet(id, `/assets/${id}_sheet.png`, DEFAULT_WALK, DEFAULT_IDLE);
  loadSkinSheets(id, `/assets/${id}_sheet.png`, DEFAULT_WALK, DEFAULT_IDLE);
}

// Lista dos personagens com atlas (pra páginas de debug/preview)
export const SHEET_CHAR_IDS: readonly string[] = SHEET_CHARS;

// null enquanto o PNG não carregou (ou se o personagem não tem atlas).
// Com skin escolhida, só devolve o atlas DA SKIN: enquanto ele não fica
// pronto, o chamador cai no sprite procedural da skin (nunca no traje errado).
export function charSheet(charId: string, skinId: string = DEFAULT_SKIN): CharSheet | null {
  return sheets.get(sheetKey(charId, skinId)) ?? null;
}

// Canvas do atlas da skin (AttackPreview desenha direto dele); null carregando
export function skinSheetCanvas(charId: string, skinId: string): HTMLCanvasElement | null {
  return skinCanvases.get(sheetKey(charId, skinId)) ?? null;
}

// dataURL do atlas da skin pro DOM (retratos via CSS) — toDataURL é caro,
// então memoiza; null enquanto o atlas base não carregou
export function skinSheetURL(charId: string, skinId: string): string | null {
  const key = sheetKey(charId, skinId);
  const cached = skinURLs.get(key);
  if (cached) return cached;
  const canvas = skinCanvases.get(key);
  if (!canvas) return null;
  const url = canvas.toDataURL();
  skinURLs.set(key, url);
  return url;
}
